"use client";

import type { WhiteboardRenderState } from "@/lib/whiteboard/boardState";
import type { StudyExercise } from "@/lib/whiteboard/studySpec";
import React, { useState } from "react";
import { StudyPractice } from "./StudyPractice";
import type { WhiteboardCardEventHandler } from "./types";

export const studySecondary =
  "min-h-11 rounded-lg border border-[#3A3A3C] px-4 py-2 text-sm font-semibold text-[#F9F9F9] hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FD954F] disabled:opacity-40";
export const studyPrimary =
  "min-h-11 rounded-lg bg-[#FD954F] px-6 py-2 text-sm font-semibold text-[#1C1C1E] hover:bg-[#F28747] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FD954F] disabled:opacity-40";

/** Literal text only: generated emphasis never becomes markup or executable HTML. */
export function StudyText({ text, focus = [] }: { text: string; focus?: string[] }) {
  const terms = [...new Set(focus.filter(Boolean))].sort((a, b) => b.length - a.length);
  const parts: React.ReactNode[] = [];
  let start = 0;
  while (start < text.length) {
    let position = text.length;
    let match = "";
    for (const term of terms) {
      const found = text.indexOf(term, start);
      if (found >= 0 && found < position) {
        position = found;
        match = term;
      }
    }
    parts.push(text.slice(start, position));
    if (!match) break;
    parts.push(
      <mark
        key={position}
        className="rounded-sm bg-[#FD954F]/15 px-0.5 text-inherit underline decoration-[#FD954F] decoration-2 underline-offset-4"
      >
        {match}
      </mark>
    );
    start = position + match.length;
  }
  return <>{parts}</>;
}

const familyNames = {
  reading: "Reading",
  conjugation: "Conjugation",
  gap_fill: "Fill the gaps",
  word_tiles: "Sentence order",
};

export function StudyCard({
  exercise,
  tiles,
  renderState,
  onEvent,
  headerExtra,
}: {
  exercise: StudyExercise;
  tiles?: string[];
  renderState: WhiteboardRenderState;
  onEvent?: WhiteboardCardEventHandler;
  headerExtra?: React.ReactNode;
}) {
  const [hintOpen, setHintOpen] = useState(false);
  const [vocabularyOpen, setVocabularyOpen] = useState(false);
  const [discussing, setDiscussing] = useState(false);
  const hint = renderState.annotation || exercise.hint;
  const focus = [...renderState.highlightTargets, ...renderState.marks.map((mark) => mark.target)];
  return (
    <section
      aria-label={`${familyNames[exercise.family]}: ${exercise.title}`}
      className="w-full min-w-0 overflow-hidden rounded-lg border border-[#3A3A3C] bg-[#15161B] text-[#F9F9F9] [overflow-wrap:anywhere]"
    >
      <header className="flex min-h-12 items-center justify-between gap-4 border-b-2 border-[#FD954F] px-5 py-3 sm:px-7">
        <span className="text-base font-bold">ChickyTutor</span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#AEAEB2]">
            {exercise.level} · {familyNames[exercise.family]}
          </span>
          {headerExtra}
        </div>
      </header>
      <div className="space-y-6 p-5 sm:space-y-7 sm:p-7">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold leading-tight sm:text-3xl">{exercise.title}</h2>
          <p className="text-base leading-relaxed text-[#AEAEB2]">{exercise.instruction}</p>
        </div>
        {exercise.family === "reading" ? (
          <>
            <p
              className="whitespace-pre-line text-lg leading-[1.85] sm:text-xl"
              data-whiteboard-target-id="reading_story.story"
            >
              <StudyText text={exercise.story} focus={[...exercise.focus, ...focus]} />
            </p>
            {vocabularyOpen && (
              <dl className="space-y-3 border-y border-[#3A3A3C] py-4">
                {exercise.vocabulary.map((note) => (
                  <div key={note.term} className="flex flex-wrap gap-x-2">
                    <dt className="font-semibold">{note.term}</dt>
                    <dd className="text-[#AEAEB2]">{note.meaning}</dd>
                  </div>
                ))}
              </dl>
            )}
            <div className="space-y-2 border-t border-[#3A3A3C] pt-5">
              <p className="text-lg font-semibold">{exercise.question}</p>
              <p className="text-sm text-[#AEAEB2]">Answer aloud with Chicky.</p>
            </div>
          </>
        ) : (
          <StudyPractice exercise={exercise} tiles={tiles} focus={focus} onEvent={onEvent} />
        )}
        {(hintOpen || renderState.annotation) && hint && (
          <p
            role="status"
            className="border-l-2 border-[#FD954F] pl-4 text-sm leading-relaxed text-[#D1D1D6]"
          >
            {hint}
          </p>
        )}
        <footer className="flex flex-wrap items-center gap-3 border-t border-[#3A3A3C] pt-4">
          {exercise.family === "reading" && exercise.vocabulary.length > 0 && (
            <button
              type="button"
              className={studySecondary}
              aria-expanded={vocabularyOpen}
              onClick={() => {
                setVocabularyOpen(!vocabularyOpen);
                if (!vocabularyOpen)
                  onEvent?.({
                    event: "working",
                    action: "vocabulary_revealed",
                    response: exercise.vocabulary.map((note) => note.term).join(", "),
                    resolved: false,
                  });
              }}
            >
              Vocabulary
            </button>
          )}
          {hint && (
            <button
              type="button"
              className={studySecondary}
              aria-expanded={hintOpen}
              onClick={() => {
                setHintOpen(!hintOpen);
                if (!hintOpen)
                  onEvent?.({
                    event: "working",
                    action: "hint_revealed",
                    response: hint,
                    resolved: false,
                  });
              }}
            >
              Hint
            </button>
          )}
          {exercise.family === "reading" && (
            <button
              type="button"
              disabled={discussing}
              className={`${studyPrimary} ml-auto`}
              onClick={() => {
                setDiscussing(true);
                onEvent?.({
                  event: "reading_complete",
                  action: "discuss_story",
                  response: exercise.question,
                  resolved: false,
                });
              }}
            >
              {discussing ? "Discuss with Chicky" : "Discuss"}
            </button>
          )}
          <span className="text-xs leading-relaxed text-[#AEAEB2]">{exercise.objective}</span>
        </footer>
      </div>
    </section>
  );
}
